import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { submitCupCustom } from "@/lib/api";

const SIZES = [55, 65, 75, 90, 150, 210];
const COLORS = [
  { name: "White", hex: "#FFFFFF" },
  { name: "Ivory", hex: "#F8F1E3" },
  { name: "Kraft", hex: "#C7A074" },
  { name: "Charcoal", hex: "#1F1F1F" },
  { name: "Brand Pink", hex: "#FF1493" },
  { name: "Brand Orange", hex: "#FF7A1A" },
  { name: "Brand Yellow", hex: "#FFC419" },
  { name: "Forest", hex: "#0F5132" },
  { name: "Sky", hex: "#3B82F6" },
  { name: "Crimson", hex: "#B91C1C" },
];

export const CupCustomizer = ({ openQuoteDialog }) => {
  const [size, setSize] = useState(150);
  const [color, setColor] = useState("#FFFFFF");
  const [text, setText] = useState("Your Brand");
  const [logo, setLogo] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", quantity: "5000", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const isDark = ["#1F1F1F", "#0F5132", "#B91C1C", "#FF1493"].includes(color.toUpperCase());
  const textColor = isDark ? "#FFFFFF" : "#171717";

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLogo(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await submitCupCustom({
        ...form,
        size_ml: size,
        color_hex: color,
        text_label: text,
        logo_data_url: logo || "",
      });
      toast.success("Customization request received! Our team will reach out within 24 hrs.");
      setForm({ name: "", company: "", email: "", phone: "", quantity: "5000", notes: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="customize" data-testid="cup-customizer" className="py-24 md:py-32 bg-gradient-to-b from-white via-neutral-50/40 to-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="font-mono-vc text-xs uppercase tracking-[0.25em] vc-text-gradient font-bold">
            Live Cup Customizer
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-extrabold text-neutral-900">
            Design your <span className="vc-text-gradient">signature paper cup</span>.
          </h2>
          <p className="mt-5 text-neutral-600 text-base md:text-lg">
            Pick a size, choose your brand color, upload your logo — see it come alive in real time.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Preview */}
          <div className="lg:col-span-5 rounded-3xl border border-neutral-200 bg-white p-10 relative overflow-hidden">
            <div className="absolute inset-0 vc-grid-bg opacity-50" />
            <div className="relative h-full flex flex-col items-center justify-center min-h-[440px]">
              <div className="vc-cup" style={{ "--cup-color": color }}>
                <div className="vc-steam"><span /><span /><span /></div>
                <div className="rim"><div className="rim-inner" /></div>
                <div className="body">
                  <div className="logo-wrap">
                    {logo ? (
                      <img src={logo} alt="Logo" />
                    ) : (
                      <span className="label-text" style={{ color: textColor }}>{text || "Your Brand"}</span>
                    )}
                  </div>
                  <div className="size-tag" style={{ color: textColor }}>{size} ML • VERSACORP</div>
                </div>
                <div className="base" />
              </div>
              <div className="mt-6 flex items-center gap-2">
                <Sparkles size={14} className="text-orange-500" />
                <span className="text-xs font-mono-vc uppercase tracking-[0.18em] text-neutral-500">
                  Live preview — updates instantly
                </span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="lg:col-span-7 rounded-3xl border border-neutral-200 bg-white p-7 md:p-9">
            {/* Sizes */}
            <div>
              <Label className="text-sm font-semibold text-neutral-900">Cup Size</Label>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`cup-size-${s}`}
                    onClick={() => setSize(s)}
                    className={`h-12 rounded-xl border text-sm font-bold font-mono-vc transition-all ${
                      size === s
                        ? "vc-gradient text-white border-transparent shadow-lg shadow-orange-500/20"
                        : "bg-white text-neutral-800 border-neutral-200 hover:border-neutral-400"
                    }`}
                  >
                    {s}ml
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="mt-7">
              <Label className="text-sm font-semibold text-neutral-900">Cup Color</Label>
              <div className="mt-3 flex flex-wrap gap-3">
                {COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    data-testid={`cup-color-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setColor(c.hex)}
                    title={c.name}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      color === c.hex ? "scale-110 border-neutral-900 shadow-md" : "border-neutral-200 hover:scale-105"
                    }`}
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
            </div>

            {/* Branding */}
            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="brand-text" className="text-sm font-semibold text-neutral-900">Brand Text (optional)</Label>
                <Input
                  id="brand-text"
                  data-testid="cup-brand-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Your Brand"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-neutral-900">Brand Logo (upload)</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="cup-logo-input" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex-1 h-10 rounded-md border border-dashed border-neutral-300 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 flex items-center justify-center gap-2"
                    data-testid="cup-logo-upload-btn"
                  >
                    <Upload size={15} /> {logo ? "Replace logo" : "Upload logo"}
                  </button>
                  {logo && (
                    <button
                      type="button"
                      onClick={() => setLogo(null)}
                      className="h-10 px-3 rounded-md border border-neutral-200 text-neutral-700"
                      data-testid="cup-logo-clear"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Lead form */}
            <form onSubmit={handleSubmit} className="mt-8 pt-7 border-t border-neutral-200" data-testid="cup-custom-form">
              <div className="font-display text-lg font-bold text-neutral-900 mb-4">
                Get pricing for this design
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="cup-form-name" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                <Input data-testid="cup-form-company" placeholder="Company / Brand" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <Input data-testid="cup-form-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                <Input data-testid="cup-form-phone" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                <Input data-testid="cup-form-quantity" placeholder="Quantity (pcs)" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                <Input data-testid="cup-form-notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="mt-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="text-xs text-neutral-500 font-mono-vc uppercase tracking-wider">
                  Spec → {size}ml • {color} • {logo ? "Logo uploaded" : `Text: "${text}"`}
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="cup-custom-submit"
                  className="vc-gradient text-white rounded-full h-11 px-7 font-semibold border-0"
                >
                  {submitting ? "Submitting..." : "Submit Customization Request"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
